import type { FieldValue } from 'firebase-admin/firestore'
import type {
  DocumentData,
  Firestore,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { collection, doc, serverTimestamp } from 'firebase/firestore'
import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import { GradeSyncJobsCubit } from '../cubits/GradeSyncJobsCubit'
import { NotficationsCubit } from '../cubits/NotificationsCubit'
import {
  getDocWithError,
  setDocWithError,
  updateDocWithError,
} from '../firestore-mobx/fetch'
import {
  fetchAllOrganizationAssociatedWithUserId,
  fetchOrganizationHideDefaultProducts,
  getOrganizationAdminsByUserId,
  getOrganizationInstructorsByUserId,
  getOrganizationStreamsByIds,
} from '../firestore/Organization'
import { getPublicUser, getUsers } from '../firestore/PublicUser'
import { fetchRoomState } from '../firestore/RoomState'
import { getSettingsSuperAdmin } from '../firestore/SettingsSuperAdmin'
import {
  fetchUserHideDefaultProducts,
  getAssistedInstructorIDs,
  getUserProfile,
} from '../firestore/UserProfile'
import { getUserPromotions } from '../firestore/UserPromotion/index'
import { getUserPromotionRedemptions } from '../firestore/UserPromotionRedemption'
import {
  getUserProfileCustomProducts,
  fetchUserProfileCustomProducts,
} from '../firestore/UserProfileCustomProduct'
import { getProducts } from '../firestore/Product'
import { getSectionProductsBySectionId } from '../firestore/SectionProduct'
import { UserProfileTokens } from '../stores/UserProfileTokens'
import type { StaticModelCollection } from '../types'
import {
  DocumentDoesNotExistError,
  InvitationConsumeResult,
  SuperAdminPrivilege,
  UserProfileRole,
  type FetchOrganizationCustomProductsForInstructorResult,
  type FetchOrganizationCustomProductsForInstructorArgs,
} from '../types'
import { UnsubscribeManager } from '../util/UnsubscribeManager'
import type { FirebaseRepository } from './FirebaseRepository'
import { OrganizationAdmin } from './OrganizationAdmin'
import { OrganizationInstructor } from './OrganizationInstructor'
import { PublicUser } from './PublicUser'
import { SettingsSuperAdmin } from './SettingsSuperAdmin'
import { UserProfile } from './UserProfile'
import { UserPromotion } from './UserPromotion'
import { UserPromotionRedemption } from './UserPromotionRedemption'
import { UserProfileCustomProduct } from './UserProfileCustomProduct'
import { Product } from './Product'
import { SectionProduct } from './SectionProduct'
import { getCatalogsForUser } from '../firestore/Catalog'
import {
  batchUpdateSectionCollaborators,
  getCollaboratorUserIdsStream,
  getSectionsById,
  removeCollaboratorFromInstructor,
} from '../firestore/Section'
import { Catalog } from './Catalog'
import { Section } from './Section'
import { guardOn, guardOnLoaded } from '../util/guardOn'
import { fetchOrganizationCustomProducts } from '../firestore/OrganizationCustomProduct'
import { OrganizationCustomProduct } from './OrganizationCustomProduct'
import { Organization } from './Organization'
import { httpsCallable } from 'firebase/functions'
import type { MobxDocument } from '../types'
import { type FirestoreOrganizationCustomProduct } from '../firestore/OrganizationCustomProduct'

import { deleteAssistantship } from '../firestore/UserProfileInstructor'
import { updateEmail } from '../firestore/UserProfile'
import {
  fetchUserProfileScoreMappingDefaults,
  type FirestoreUserProfileScoreMappingDefaults,
} from '../firestore/UserProfileScoreMappingDefaults'
import { noTryAsync } from '../util'
import { fetchInvitation } from '../firestore/Invitation'
import { type FirestoreInvitation } from '../firestore/Invitation/schema'

type CreateInvitationResult =
  | {
      result: InvitationConsumeResult.success
      invitation?: FirestoreInvitation
    }
  | {
      result: InvitationConsumeResult.errorAlreadyConsumed
      invitation?: FirestoreInvitation
    }
  | {
      result:
        | InvitationConsumeResult.errorAlreadyConsumed
        | InvitationConsumeResult.errorCreatedByYou
        | InvitationConsumeResult.errorInvitationExpired
        | InvitationConsumeResult.errorInvitationNotFound
        | InvitationConsumeResult.errorPromotionAlreadyConsumed
    }

// TODO: move to stores
/**
 * A BreakoutUser is a user of the Breakout platform, it is composed of a user profile and a public user.
 **/
export class BreakoutUser {
  uid: string
  firestore: Firestore
  repository: FirebaseRepository

  private userProfileTokens: UserProfileTokens
  private _onboardCompleteLocal = false
  private userStreamSubscription: StreamSubscriptionActions | undefined
  private profileStreamSubscription: StreamSubscriptionActions | undefined
  private userPromotionsStream: StreamSubscriptionActions | undefined
  private _userPromotions: StaticModelCollection<UserPromotion>
  private userPromotionRedemptionsStream: StreamSubscriptionActions | undefined
  private _userPromotionRedemptions: StaticModelCollection<UserPromotionRedemption>
  private userCustomProductsStream: StreamSubscriptionActions | undefined
  private _userCustomProducts: StaticModelCollection<UserProfileCustomProduct>
  private productsStream: StreamSubscriptionActions | undefined
  private _products: StaticModelCollection<Product>
  private sectionProductsStream: StreamSubscriptionActions | undefined
  private _sectionProducts: StaticModelCollection<SectionProduct>
  private _currentSectionId: string | undefined

  private _assistedUsersSubscription: StreamSubscriptionActions | undefined
  private _userAssistedUserIdsSubscription:
    | StreamSubscriptionActions
    | undefined

  private _collaboratorUsersSubscription: StreamSubscriptionActions | undefined
  private _collaboratorUserIdsSubscription:
    | StreamSubscriptionActions
    | undefined

  private _superAdminSubscription: StreamSubscriptionActions | undefined

  private _userProfile: UserProfile
  private _organizationAdmins: StaticModelCollection<OrganizationAdmin>
  private _organizationInstructors: StaticModelCollection<OrganizationInstructor>

  private _usersIAssist: StaticModelCollection<PublicUser>

  private _usersICollaborateWith: StaticModelCollection<PublicUser>
  private _organizations: StaticModelCollection<Organization>
  private _organizationsStream: StreamSubscriptionActions | undefined
  @observable private _loadedOrganizationIds: string[] = []

  private _catalogs: StaticModelCollection<Catalog>
  private _sharedSections: StaticModelCollection<Section>
  private _catalogsSubscription: StreamSubscriptionActions | undefined
  private _catalogSectionsSubscription: StreamSubscriptionActions | undefined

  publicUser: PublicUser
  superAdmin: SettingsSuperAdmin
  @observable
  invokedConsumeInvitation = false

  private _notifications: NotficationsCubit | null = null
  private _gradeSyncJobs: GradeSyncJobsCubit | null = null

  unsubscribers = new UnsubscribeManager()

  constructor(repository: FirebaseRepository, uid: string) {
    this.uid = uid
    this.repository = repository
    const firestore = repository.firestore
    this.firestore = firestore
    this.userProfileTokens = new UserProfileTokens(repository, this)
    this.publicUser = PublicUser.empty(repository)

    this._userProfile = UserProfile.empty(repository)
    this._userPromotions = UserPromotion.emptyCollection(repository)
    this._userPromotionRedemptions =
      UserPromotionRedemption.emptyCollection(repository)
    this._userCustomProducts =
      UserProfileCustomProduct.emptyCollection(repository)
    this._products = Product.emptyCollection(repository)
    this._sectionProducts = SectionProduct.emptyCollection(repository)
    this._usersIAssist = PublicUser.emptyCollection(repository)
    this._usersICollaborateWith = PublicUser.emptyCollection(repository)
    this._organizationAdmins = OrganizationAdmin.emptyCollection(repository)
    this._organizationInstructors =
      OrganizationInstructor.emptyCollection(repository)
    this.superAdmin = SettingsSuperAdmin.empty(repository)
    this._usersIAssist = PublicUser.emptyCollection(repository)
    this._organizations = Organization.emptyCollection(repository)

    this._catalogs = Catalog.emptyCollection(repository)
    this._sharedSections = Section.emptyCollection(repository)

    makeObservable(this)

    const orgStreamDisposer = reaction(
      () => this.organizationIds,
      (organizationIds) => {
        this._organizationsStream?.cancel()
        this._organizationsStream = undefined
        if (organizationIds.length > 0) {
          this._organizationsStream = getOrganizationStreamsByIds(
            this.repository,
            organizationIds
          ).listen((organizations) => {
            runInAction(() => {
              this._organizations.replaceModels(organizations)
              this._loadedOrganizationIds = organizationIds
            })
          })
        } else {
          runInAction(() => {
            this._organizations.replaceModels([])
            this._loadedOrganizationIds = organizationIds
          })
        }
      },
      { equals: comparer.structural }
    )
    this.unsubscribers.add(() => orgStreamDisposer())
  }

  initialize() {
    this.userProfileTokens.initialize()
  }

  loadUserProfileTokenData() {
    this.userProfileTokens.startTokenStreamIfNotStarted()
    this.userProfileTokens.startPurchasesStreamIfNotStarted()
    this.userProfileTokens.startSectionPassesStreamIfNotStarted()
  }

  // Lazy loaded, but guaranteed NotficationsCubit
  @computed
  get notifications(): NotficationsCubit {
    let notifications = this._notifications
    if (!notifications) {
      notifications = new NotficationsCubit({ repository: this.repository })
      notifications.safeStartNotificationsStream()
      this._notifications = notifications
    }
    return notifications
  }

  // Lazy loaded GradeSyncJobsCubit for async grade re-sync operations
  @computed
  get gradeSyncJobs(): GradeSyncJobsCubit {
    let gradeSyncJobs = this._gradeSyncJobs
    if (!gradeSyncJobs) {
      gradeSyncJobs = new GradeSyncJobsCubit({ repository: this.repository })
      gradeSyncJobs.startJobsStream()
      this._gradeSyncJobs = gradeSyncJobs
    }
    return gradeSyncJobs
  }

  startUserStreamIfNotRunning() {
    if (!this.uid) return
    if (this.userStreamSubscription) return

    const userStream = getPublicUser(this.repository, { userId: this.uid })
    this.userStreamSubscription = userStream.listen((user) => {
      this.publicUser.replaceModel(user)
    })
  }

  startProfileIfNotRunning() {
    if (!this.uid) return
    if (this.profileStreamSubscription) return

    const profileStream = getUserProfile(this.repository, { userId: this.uid })
    this.profileStreamSubscription = profileStream.listen((profile) => {
      this._userProfile.replaceModel(profile)
      this.startAssistedUsersStream()
      this.startOrganizationStreams()
      this.startSuperAdminStream()
      this.startCollaboratorStream()
    })
  }

  isAssistantOfInstructor(instructorId: string) {
    return this._usersIAssist.models.some((user) => user.id === instructorId)
  }

  startAssistedUsersStream() {
    // never start twice
    if (this._userAssistedUserIdsSubscription) return

    // If user is student, do not start assisted users stream
    if (this.isStudent) {
      this._usersIAssist.replaceModels([])
      return
    }

    const assistedUserIdsStream = getAssistedInstructorIDs(
      this.repository,
      this.uid
    )
    this._userAssistedUserIdsSubscription = assistedUserIdsStream.listen(
      (userIds) => {
        this._assistedUsersSubscription?.cancel()
        if (userIds.length === 0) {
          this._usersIAssist.replaceModels([])
          return
        }

        const instructorsStream = getUsers(this.repository, {
          userIds: userIds,
        })

        this._assistedUsersSubscription = instructorsStream.listen((users) => {
          this._usersIAssist.replaceModels(users)
        })

        this.unsubscribers.add(() => this._assistedUsersSubscription?.cancel())
      }
    )

    this.unsubscribers.add(() =>
      this._userAssistedUserIdsSubscription?.cancel()
    )
  }

  startCollaboratorStream() {
    if (this._collaboratorUserIdsSubscription) return

    const isStudent = this.isStudent
    if (isStudent) {
      this._usersICollaborateWith.replaceModels([])
      return
    }

    const stream = getCollaboratorUserIdsStream(this.repository, undefined) // No impersonation for BreakoutUser
    this._collaboratorUserIdsSubscription = stream.listen((instructorIds) => {
      this._collaboratorUsersSubscription?.cancel()

      if (instructorIds.length === 0) {
        this._usersICollaborateWith.replaceModels([])
        return
      }

      const instructorsStream = getUsers(this.repository, {
        userIds: instructorIds,
      })

      this._collaboratorUsersSubscription = instructorsStream.listen(
        (instructors) => {
          this._usersICollaborateWith.replaceModels(instructors)
        }
      )
      this.unsubscribers.add(() =>
        this._collaboratorUsersSubscription?.cancel()
      )
    })
    this.unsubscribers.add(() =>
      this._collaboratorUserIdsSubscription?.cancel()
    )
  }

  startSuperAdminStream() {
    if (!this.uid) return
    // only admins can be super admins, so don't even bother if not admin
    if (!this.isAdmin) return

    const instructorStream = getSettingsSuperAdmin(this.repository)
    this._superAdminSubscription = instructorStream.listen((admin) => {
      this.superAdmin.replaceModel(admin)
    })

    // Add named subscription
    this.unsubscribers.add(() => this._superAdminSubscription?.cancel())
  }

  startUserPromotionsStreamIfNotRunning() {
    if (!this.uid) return
    if (this.userPromotionsStream) return

    const userPromotionStream = getUserPromotions(this.repository, {
      userId: this.uid,
    })
    this.userPromotionsStream = userPromotionStream.listen((promotions) => {
      this._userPromotions.replaceModels(promotions)
    })
  }

  startUserPromotionRedemptionsStreamIfNotRunning() {
    if (!this.uid) return
    if (this.userPromotionRedemptionsStream) return

    const redemptionsStream = getUserPromotionRedemptions(this.repository, {
      userId: this.uid,
    })

    this.userPromotionRedemptionsStream = redemptionsStream.listen(
      (promotions) => this._userPromotionRedemptions.replaceModels(promotions)
    )
  }

  startUserCustomProductsStreamIfNotRunning() {
    if (!this.uid) return
    if (this.userCustomProductsStream) return

    const customProductsStream = getUserProfileCustomProducts(this.repository, {
      userId: this.uid,
    })

    this.userCustomProductsStream = customProductsStream.listen(
      (customProducts: UserProfileCustomProduct[]) =>
        this._userCustomProducts.replaceModels(customProducts)
    )
  }

  startProductsStreamIfNotRunning() {
    if (this.productsStream) return

    const productsStream = getProducts(this.repository)

    this.productsStream = productsStream.listen((products) =>
      this._products.replaceModels(products)
    )
  }

  startSectionProductsStreamIfNotRunning(sectionId: string) {
    if (this.sectionProductsStream && this._currentSectionId === sectionId)
      return

    // Cancel existing stream if we're switching sections
    this.sectionProductsStream?.cancel()
    this._currentSectionId = sectionId

    const sectionProductsStream = getSectionProductsBySectionId(
      this.repository,
      {
        sectionId,
      }
    )

    this.sectionProductsStream = sectionProductsStream.listen(
      (sectionProducts) => this._sectionProducts.replaceModels(sectionProducts)
    )
  }

  @computed
  get usersIAssist() {
    this.startProfileIfNotRunning()
    return guardOnLoaded(this.profile, () => this._usersIAssist.models)
  }

  @computed
  get usersICollaborateWith() {
    this.startProfileIfNotRunning()
    return guardOnLoaded(this.profile, () => this._usersICollaborateWith.models)
  }

  @computed
  get allRelatedInstructors() {
    return guardOnLoaded(
      [this._usersIAssist, this._usersICollaborateWith],
      ([usersIAssist, usersICollaborateWith]) => {
        const usersIHelp = [...usersIAssist, ...usersICollaborateWith]

        const instructorIds = new Set<string>()
        return usersIHelp.filter((instructor) => {
          if (instructorIds.has(instructor.id)) {
            return false
          }
          instructorIds.add(instructor.id)
          return true
        })
      }
    )
  }

  get isSuperAdmin() {
    return this.superAdmin.isLoaded
  }

  get canEditOutputPrompt() {
    return (
      this.isAdmin &&
      this.superAdmin.isLoaded &&
      this.superAdmin.hasPrivilege(SuperAdminPrivilege.canEditOutputPrompt)
    )
  }

  get canInstallPrompts() {
    return (
      this.isAdmin &&
      this.superAdmin.isLoaded &&
      this.superAdmin.hasPrivilege(SuperAdminPrivilege.canInstallPrompts)
    )
  }

  get canAlwaysApplyAdminApprovedFreePromotion() {
    return (
      this.isAdmin &&
      this.superAdmin.isLoaded &&
      this.superAdmin.hasPrivilege(
        SuperAdminPrivilege.canAlwaysApplyAdminApprovedFreePromotion
      )
    )
  }

  @computed
  get hasAccessToRoomInspector() {
    return this.isAdmin
  }

  @computed
  get canInspectUsers() {
    return (
      this.isAdmin &&
      this.superAdmin.isLoaded &&
      this.superAdmin.hasPrivilege(SuperAdminPrivilege.userInspector)
    )
  }

  startOrganizationStreams() {
    const role = this.profile?.data?.role
    const isValidRole = [
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.instructor,
    ].includes(role)

    // if not valid, mark streams as empty
    if (!isValidRole) {
      this._organizationAdmins.replaceModels([])
      this._organizationInstructors.replaceModels([])
      return
    }

    const organizationAdminSubscription = getOrganizationAdminsByUserId(
      this.repository,
      this.uid
    ).listen((admins) => {
      this._organizationAdmins.replaceModels(admins)
    })

    const organizationInstructorSubscription =
      getOrganizationInstructorsByUserId(this.repository, this.uid).listen(
        (instructors) => {
          this._organizationInstructors.replaceModels(instructors)
        }
      )

    this.unsubscribers.add(() => organizationAdminSubscription.cancel())
    this.unsubscribers.add(() => organizationInstructorSubscription.cancel())
  }

  dispose() {
    this.userProfileTokens.dispose()
    this.userStreamSubscription?.cancel()
    this.profileStreamSubscription?.cancel()
    this.userPromotionsStream?.cancel()
    this.userPromotionRedemptionsStream?.cancel()
    this.userCustomProductsStream?.cancel()
    this.productsStream?.cancel()
    this.sectionProductsStream?.cancel()
    this._superAdminSubscription?.cancel()
    this._organizationsStream?.cancel()
    this._catalogsSubscription?.cancel()
    this._catalogSectionsSubscription?.cancel()
    this.unsubscribers.dispose()
    this.notifications.dispose()
    this._collaboratorUserIdsSubscription?.cancel()
    this._collaboratorUsersSubscription?.cancel()
  }

  get promotionsAndRedemptionsLoaded() {
    return (
      this.userPromotions.isLoaded && this.userPromotionRedemptions.isLoaded
    )
  }

  @computed
  get organizationAdmins() {
    return this._organizationAdmins
  }

  @computed
  get organizationInstructors() {
    return this._organizationInstructors
  }

  get isOrganizationMember() {
    return (
      this.organizationAdmins.models.length > 0 ||
      this.organizationInstructors.models.length > 0
    )
  }

  get organizationIds() {
    const concatted = [
      ...this.organizationAdmins.models.map(
        (admin) => admin.data.organizationId
      ),
      ...this.organizationInstructors.models.map(
        (instructor) => instructor.data.organizationId
      ),
    ]

    return Array.from(new Set(concatted)).sort()
  }

  @computed
  get organizations() {
    const orgsLoaded = comparer.structural(
      this.organizationIds,
      this._loadedOrganizationIds
    )
    return guardOn(
      this.organizationAdmins.isLoaded &&
        this.organizationInstructors.isLoaded &&
        orgsLoaded,
      () => this._organizations
    )
  }

  async fetchOrganizationsByUserId(userId: string) {
    return fetchAllOrganizationAssociatedWithUserId(this.repository, userId)
  }

  isOrganizationAdmin(organizationId: string) {
    return this.organizationAdmins.models.some(
      (admin) => admin.data.organizationId === organizationId
    )
  }

  isOrganizationInstructor(organizationId: string) {
    return this.organizationInstructors.models.some(
      (instructor) => instructor.data.organizationId === organizationId
    )
  }

  @computed
  get userPromotions() {
    this.startUserPromotionsStreamIfNotRunning()
    return this._userPromotions
  }

  @computed
  get userPromotionRedemptions() {
    this.startUserPromotionRedemptionsStreamIfNotRunning()
    return this._userPromotionRedemptions
  }

  getMostRecentSectionProduct(sectionId: string) {
    this.startSectionProductsStreamIfNotRunning(sectionId)

    const sectionProducts = this._sectionProducts

    return guardOn(sectionProducts.isLoaded, () => {
      if (sectionProducts.models.length === 0) {
        return null
      }

      const sortedProducts = [...sectionProducts.models].sort((a, b) => {
        const aTime = a.data.updatedAt?.getTime() || 0
        const bTime = b.data.updatedAt?.getTime() || 0
        return bTime - aTime
      })

      return sortedProducts[0]
    })
  }

  @computed
  get userPromotionRedemptionsByUserPromotionId() {
    return this.userPromotionRedemptions.models.reduce((acc, redemption) => {
      const promotionId = redemption.data.userPromotionId
      if (!acc.has(promotionId)) acc.set(promotionId, [])
      acc.get(promotionId)?.push(redemption)
      return acc
    }, new Map<string, UserPromotionRedemption[]>())
  }

  @computed
  get user() {
    this.startUserStreamIfNotRunning()
    return this.publicUser
  }

  @computed
  get mappedSlideDecksToConsumedTokens() {
    return this.userProfileTokens.mappedSlideDecksToConsumedTokens
  }

  @computed
  get profile() {
    this.startProfileIfNotRunning()
    return this._userProfile
  }

  get isLtiUser() {
    return (
      this.profile.data.canvasUserId !== undefined ||
      this.profile.data.ltiUserId !== undefined
    )
  }

  get photoURL() {
    return this.user.data.imageUrl
  }

  @computed
  get defaultSystemProducts() {
    this.startProductsStreamIfNotRunning()
    return guardOn(this._products.isLoaded, () => this._products)
  }

  @computed
  get productsCollection() {
    this.startProductsStreamIfNotRunning()
    return this._products
  }

  @computed
  get userCustomProducts() {
    this.startUserCustomProductsStreamIfNotRunning()
    return guardOn(
      this._userCustomProducts.isLoaded,
      () => this._userCustomProducts
    )
  }

  @computed
  get shouldHideDefaultProducts() {
    return guardOn(
      this.profile.isLoaded,
      () => this.profile.data.hideDefaultProducts
    )
  }

  /**
   * Waits for a room state to be available in the repository before proceeding.
   *
   * Repeatedly attempts to fetch the room state by `roomStateId` until it succeeds
   * or the given timeout is reached. Checks every second after a failed fetch,
   * unless the error is `DocumentDoesNotExistError`, in which case it rejects immediately.
   */

  waitForRoomStateLoaded(
    roomStateId: string,
    { timeout }: { timeout: number } = { timeout: 10000 }
  ) {
    return new Promise<void>((resolve, reject) => {
      const startTime = Date.now()
      const checkStatus = () => {
        if (Date.now() - startTime > timeout) {
          reject(new Error('waitForLoaded timed out'))
          return
        }
        fetchRoomState(this.repository, {
          roomStateId: roomStateId,
        })
          .then(() => {
            resolve()
          })
          .catch((err) => {
            if (err instanceof DocumentDoesNotExistError) {
              return reject(err)
            }
            setTimeout(checkStatus, 1000)
          })
      }
      checkStatus()
    })
  }

  async consumeInvitation(
    invitationId: string
  ): Promise<CreateInvitationResult> {
    if (!this.uid) {
      throw new Error('User must be logged in to consume invitation')
    }

    const collectionRef = collection(
      this.firestore,
      'invitation'
    ).withConverter({
      toFirestore: (data) => data,
      fromFirestore: (
        snap: QueryDocumentSnapshot<DocumentData, DocumentData>
      ) => snap.data() as FirestoreInvitation,
    })
    try {
      await this.userPromotions.waitForLoaded()
      const collisionCodes = this.userPromotions.models
        .map((p) => p.data.collisionCode?.trim())
        .filter((c): c is string => !!c)

      const invitationRef = doc(collectionRef, invitationId)

      const userInvitationRef = doc(
        collection(invitationRef, 'users'),
        this.uid
      )

      const alreadyConsumed = (
        await getDocWithError(userInvitationRef, 'FetchUserInvitationError')
      ).exists()

      if (alreadyConsumed) {
        // try and return the invitation data if we already consumed it
        const [invitation] = await noTryAsync(() =>
          fetchInvitation(this.repository, { invitationId })
        )
        return {
          result: InvitationConsumeResult.errorAlreadyConsumed,
          invitation: invitation?.data,
        }
      }

      // most of the time this will not work, we can only fetch if admin or we created in the invite
      const invitationDocInitial = await getDocWithError(
        invitationRef,
        'FetchInvitationError'
      ).catch(() => undefined)

      if (invitationDocInitial) {
        if (invitationDocInitial.exists()) {
          const invitation = invitationDocInitial.data()

          if (invitation.userId === this.uid) {
            return { result: InvitationConsumeResult.errorCreatedByYou }
          }
        } else {
          return { result: InvitationConsumeResult.errorInvitationNotFound }
        }
      }

      await setDocWithError(
        userInvitationRef,
        {
          updatedAt: serverTimestamp(),
        },
        {
          errorName: 'ConsumeInvitationError',
        }
      )

      runInAction(() => (this.invokedConsumeInvitation = true))

      let attempt = 0
      let invitation: FirestoreInvitation | undefined
      // ensure we wait for the invitation to be created - this prevents race conditions
      while (attempt < 3) {
        // still may not have sufficient permissions to fetch
        // as long as update was successful, we can return success
        const [fetchedInvitation] = await noTryAsync(() =>
          fetchInvitation(this.repository, {
            invitationId,
          })
        )

        if (fetchedInvitation) {
          invitation = fetchedInvitation.data
          break
        } else {
          attempt++
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      if (invitation && invitation.promotionId) {
        const collisionCode =
          invitation.promotionArguments?.collisionCode?.trim()
        if (collisionCode) {
          if (collisionCodes.includes(collisionCode)) {
            // we have a collision, show that to the user
            return {
              result: InvitationConsumeResult.errorPromotionAlreadyConsumed,
            }
          }
        }
      }

      this.repository.logEvent('invitation_accepted', {
        invitation_id: invitationId,
        promotion_id: invitation?.promotionId,
      })

      // we may want to log this error in the future
      // for now we wait for requires invitation to move to false
      // or the timeout threshold, whatever comes first
      await noTryAsync(() => this.waitForRequiresInvitationUnset())

      return { result: InvitationConsumeResult.success, invitation }
    } catch (e) {
      return { result: InvitationConsumeResult.errorInvitationExpired }
    }
  }

  @computed
  get userProfileTokensLoaded() {
    return (
      this.userProfileTokens.tokensCollection.isLoaded &&
      this.userProfileTokens.purchaseCollection.isLoaded &&
      this.userProfileTokens.sectionPassesCollection.isLoaded
    )
  }

  @computed
  get hasData() {
    return this.profile.hasData && this.user.hasData
  }

  @computed
  get isLoading() {
    return this.profile.isLoading || this.user.isLoading
  }

  @computed
  get isLoaded() {
    return this.profile.isLoaded && this.user.isLoaded
  }

  @computed
  get firstName() {
    return this.user.data.firstName
  }

  @computed
  get lastName() {
    return this.user.data.lastName
  }

  @computed
  get emailAddress() {
    return this.profile.data.emailAddress
  }

  @computed
  get role() {
    return this.profile.hasData ? this.profile.data.role : ''
  }

  @computed
  get roleGuarded() {
    return guardOn(this.profile.isLoaded, () => this.profile.data.role)
  }

  @computed
  get isAdmin() {
    return this.role === UserProfileRole.admin
  }

  @computed
  get isStudent() {
    return this.role === UserProfileRole.student
  }

  @computed
  get isInstructor() {
    return this.role === UserProfileRole.instructor
  }

  @computed
  get isTA() {
    return this.role === UserProfileRole.ta
  }

  @computed
  get isFaculty() {
    return this.isInstructor || this.isTA
  }

  @computed
  get shouldShowOnboarding() {
    if (!this.profile.hasData) return false

    const { verifyInvitation: verifyingInvitations } =
      this.repository.featureFlags.data

    // if consume was invoked successfully,
    // allow onboarding to show now as requiresInvitation should move to false shortly via trigger
    const userRequiresInvitation =
      (this.profile.data.requiresInvitation ?? false) &&
      !this.invokedConsumeInvitation

    return (
      this.isStudent &&
      !this.profile.data.onboardComplete &&
      !this._onboardCompleteLocal &&
      !this.isAnonymous &&
      // if not verifying invitations show onboarding
      // if verifying invitations, only show onboarding if user does not require an invitation
      (!verifyingInvitations || !userRequiresInvitation)
    )
  }

  @computed
  get tokensLoading() {
    if (!this.uid) return false

    return this.userProfileTokens.isLoading
  }

  @computed
  get tokensLoaded() {
    return !this.tokensLoading
  }

  @computed
  get purchasesLoading() {
    if (!this.uid) return false
    return this.userProfileTokens.isLoading
  }

  @computed
  get purchasesLoaded() {
    return !this.purchasesLoading
  }

  @computed
  get tokens() {
    if (!this.uid) return []
    return this.userProfileTokens.tokens
  }

  @computed
  get purchases() {
    if (!this.uid) return []
    return this.userProfileTokens.purchases
  }

  @computed
  get sectionPassSectionIds() {
    return this.userProfileTokens.sectionPassSectionIds
  }

  @computed
  get availableTokens() {
    return this.userProfileTokens.availableTokens
  }

  @computed
  get sectionPassRebateAvailableCents() {
    return this.userProfileTokens.sectionPassRebateAvailableCents
  }

  @computed
  get sectionPassRebateConsumedCents() {
    return this.userProfileTokens.sectionPassRebateConsumedCents
  }

  @computed
  get consumedTokens() {
    return this.userProfileTokens.consumedTokens
  }

  @computed
  get expiredTokens() {
    return this.userProfileTokens.expiredTokens
  }

  @computed
  get isAnonymous() {
    return this.profile.isAnonymous
  }

  @computed
  get isAnonymousDefaultNames() {
    return (
      this.isAnonymous &&
      this.emailAddress === 'demo@breakoutlearning.com' &&
      this.firstName === 'Anonymous'
    )
  }

  @computed
  get isEditor() {
    return [UserProfileRole.admin, UserProfileRole.editor].includes(
      this.profile?.data?.role ?? UserProfileRole.student
    )
  }

  @computed
  get isAuthor() {
    return [
      UserProfileRole.admin,
      UserProfileRole.editor,
      UserProfileRole.author,
    ].includes(this.profile?.data?.role ?? UserProfileRole.student)
  }

  @computed
  get isCorre() {
    return [UserProfileRole.admin, UserProfileRole.corre].includes(
      this.profile?.data?.role ?? UserProfileRole.student
    )
  }

  @computed
  get isInternal() {
    return [
      UserProfileRole.admin,
      UserProfileRole.editor,
      UserProfileRole.author,
      UserProfileRole.corre,
    ].includes(this.profile?.data?.role ?? UserProfileRole.student)
  }

  @action
  updateName = async (firstName: string, lastName: string) => {
    if (!this.uid) return

    const payload: { [key: string]: string | FieldValue } = {}

    if (firstName) payload['firstName'] = firstName
    if (lastName) payload['lastName'] = lastName

    if (Object.keys(payload).length === 0) return

    payload.updatedAt = serverTimestamp()

    const ref = doc(this.firestore, 'users', this.uid)

    await updateDocWithError(ref, payload, 'UpdateNameError')
  }

  @action
  updateCompany = async (company: string) => {
    // get user profile ref
    const ref = doc(this.firestore, 'user_profile', this.uid)

    // update the company
    return await updateDocWithError(
      ref,
      {
        company: company,
      },
      'UpdateCompanyError'
    )
  }

  @action
  updateLanguageAndOrTimezone = async (
    language: string | undefined,
    timezone: string | undefined
  ) => {
    if (!this.uid) {
      throw new Error('User must be logged in to set language or timezone')
    }

    const ref = doc(this.firestore, 'user_profile', this.uid)

    return await updateDocWithError(
      ref,
      {
        language,
        timezone,
        updatedAt: serverTimestamp(),
      },
      'UpdateLanguageAndOrTimezoneError'
    )
  }

  @action
  updateDialect = async (dialect: string) => {
    if (!this.uid) {
      throw new Error('User must be logged in to set dialect')
    }

    const ref = doc(this.firestore, 'user_profile', this.uid)

    return await updateDocWithError(
      ref,
      {
        transcriptionLanguage: [dialect], // For now the UI only lets the user select one dialect.
        updatedAt: serverTimestamp(),
      },
      'UpdateDialectError'
    )
  }

  @action
  setOnboardingComplete = async (didComplete = true) => {
    if (!this.uid) {
      throw new Error('User must be logged in to set onboarding complete')
    }

    this._onboardCompleteLocal = didComplete

    if (this.profile.data.onboardComplete === didComplete) return

    const ref = doc(this.firestore, 'user_profile', this.uid)

    // we don't need to wait for this to complete
    await updateDocWithError(
      ref,
      {
        onboardComplete: didComplete,
        updatedAt: serverTimestamp(),
      },
      'SetOnboardingCompleteError'
    )
  }

  @action
  setWalkthroughComplete = async (didComplete = true) => {
    if (!this.uid) {
      throw new Error('User must be logged in to set walkthrough complete')
    }

    const ref = doc(this.firestore, 'user_profile', this.uid)

    // we don't need to wait for this to complete
    await updateDocWithError(
      ref,
      {
        walkthroughComplete: didComplete,
        updatedAt: serverTimestamp(),
      },
      'SetWalkthroughCompleteError'
    )
  }

  private startCatalogsAndSharedSectionsStreamIfNotRunning() {
    if (this._catalogsSubscription || !(this.isInternal || this.isInstructor))
      return
    this._catalogsSubscription = getCatalogsForUser(this.repository, {
      userId: this.uid,
    }).listen((catalogs) => {
      const catalogModels = catalogs.map(({ catalog }) => catalog)
      this._catalogs.replaceModels(catalogModels)

      const sharedSectionIds = Array.from(
        new Set(
          catalogModels.flatMap(
            (catalog) => catalog.data.catalogSharedSectionIds
          )
        )
      )
      if (this._catalogSectionsSubscription)
        this._catalogSectionsSubscription.cancel()
      this._catalogSectionsSubscription = getSectionsById(
        this.repository,
        sharedSectionIds
      ).listen((sections: Section[]) => {
        this._sharedSections.replaceModels(sections)
      })
    })

    this.unsubscribers.add(() => this._catalogsSubscription?.cancel())
    this.unsubscribers.add(() => this._catalogSectionsSubscription?.cancel())
  }

  @computed
  get catalogs() {
    this.startCatalogsAndSharedSectionsStreamIfNotRunning()
    return this._catalogs
  }

  @computed
  get sharedSections() {
    this.startCatalogsAndSharedSectionsStreamIfNotRunning()
    return this._sharedSections
  }

  @computed
  get ltiSource(): string | undefined {
    return this.profile.data.ltiSource ?? undefined
  }

  @computed
  get ltiDomain(): string | undefined {
    return this.profile.data.ltiDomain ?? undefined
  }

  async fetchInstructorCustomProducts(instructorId: string) {
    return await fetchUserProfileCustomProducts(this.repository, {
      userId: instructorId,
    })
  }

  async fetchInstructorShouldHideDefaultProducts(instructorId: string) {
    return await fetchUserHideDefaultProducts(this.repository, instructorId)
  }

  /**
   * Reads this user's saved score-mapping defaults document (the per-scale
   * mappings plus the `assignFinalGrade` toggle). Returns empty defaults when
   * none have been saved yet.
   */
  async fetchScoreMappingDefaults(): Promise<FirestoreUserProfileScoreMappingDefaults> {
    const empty = { defaults: [], assignFinalGrade: false }
    if (!this.uid) return empty
    const data = await fetchUserProfileScoreMappingDefaults(this.repository, {
      userId: this.uid,
    })
    return data ?? empty
  }

  async fetchOrganizationCustomProducts({
    organizationId,
    authorizingInstructorId,
  }: {
    organizationId: string
    authorizingInstructorId?: string
  }) {
    if (authorizingInstructorId) {
      const fetcher = httpsCallable<
        FetchOrganizationCustomProductsForInstructorArgs,
        FetchOrganizationCustomProductsForInstructorResult
      >(
        this.repository.functions,
        'fetchOrganizationCustomProductsForInstructor'
      )
      const result = await fetcher({
        organizationId,
        instructorId: authorizingInstructorId,
      })
      return result.data.map((product) => {
        const productDoc = {
          id: product.productId,
          data: product,
        } as MobxDocument<FirestoreOrganizationCustomProduct>

        return new OrganizationCustomProduct(this.repository, productDoc)
      })
    }

    return await fetchOrganizationCustomProducts(
      this.repository,
      organizationId
    )
  }

  async fetchOrganizationHideDefaultProducts(organizationId: string) {
    return await fetchOrganizationHideDefaultProducts(
      this.repository,
      organizationId
    )
  }

  async retireAsAssistant(instructorUserId: string) {
    return deleteAssistantship(this.repository.firestore, {
      userId: this.uid,
      instructorUserId,
    })
  }

  async retireAsCollaborator(instructorUserId: string) {
    return removeCollaboratorFromInstructor(
      this.repository,
      this.uid,
      instructorUserId
    )
  }

  async retireAsCollaboratorFromSections(sectionIds: string[]) {
    return batchUpdateSectionCollaborators(
      this.repository,
      sectionIds.map((sectionId) => ({
        sectionId,
        assistantUserId: this.uid,
        action: 'remove',
      }))
    )
  }

  async syncProfileEmailToAuthValue() {
    // if in vitest, do not attempt to sync
    if (typeof process !== 'undefined' && process.env.VITEST_WORKER_ID) return

    // wait for profile to load
    await when(() => this.profile.isLoaded)

    // no profile
    if (!this.profile.hasData) return

    // no user -- call too early
    if (!this.repository.auth.currentUser) throw new Error('Not current user')

    // no auth email -- don't error as this is expected case with LTI users
    if (!this.repository.auth.currentUser.email) return

    // no profile email
    if (this.profile.id !== this.repository.auth.currentUser.uid) return

    const { emailAddress } = this.profile.data

    // no update to perform -- email is present and already matches auth
    if (emailAddress === this.repository.auth.currentUser.email) return

    // update the profile to match the auth email
    await updateEmail(
      this.repository,
      this.repository.uid,
      this.repository.auth.currentUser.email
    )
  }

  /**
   * Waits for the user's `requiresInvitation` field to be set to false.
   *
   * This is useful in scenarios where the user has just consumed an invitation,
   * and we need to wait for the backend to process this change before proceeding.
   *
   * Throws an error if the wait times out.
   */
  private async waitForRequiresInvitationUnset() {
    try {
      await when(
        () =>
          this.profile.isLoaded &&
          this.profile.data.requiresInvitation !== true,
        { timeout: 5000 } // 5 seconds
      )
    } catch (error) {
      throw new Error('Timed out waiting for requiresInvitation update')
    }
  }
}
